import { debounce } from "@solid-primitives/scheduled";
import { go } from "fuzzysort";
import type { Channel } from "sdk";
import { getTimestampFromUUID } from "sdk";
import {
	createMemo,
	createResource,
	createSignal,
	For,
	onMount,
	Show,
} from "solid-js";
import { useChannels, usePreferences, useThreads } from "@/api";
import { useCtx } from "@/app/context";
import { Time } from "@/atoms/Time";
import { ChannelIcon } from "@/components/shared/User";
import { useModals } from "@/contexts/modal.tsx";
import { useNavigate } from "@/contexts/router";

export const ThreadPopout = (props: { channel_id: string }) => {
	const threads2 = useThreads();
	const channels2 = useChannels();
	const ctx = useCtx();
	const [, modalctl] = useModals();
	const nav = useNavigate();

	const [search, setSearch] = createSignal("");
	const [debouncedSearch, setDebouncedSearch] = createSignal("");
	const prefsService = usePreferences();
	const prefs = prefsService.useRead();
	const openInSidebar = () => prefs.frontend.threads_sidebar_text; // TODO: use this

	const debouncedSetSearch = debounce(
		(value: string) => setDebouncedSearch(value),
		300,
	);

	const [searchResults] = createResource(debouncedSearch, async (query) => {
		if (!query.trim()) return [];
		try {
			// TODO: prevent search query injection
			// its not a *big* problem right now, but can lead to confusing errors i suppose
			const tantivyQuery = `+(${query}) +channel_id:${props.channel_id} +subtype: IN [ThreadPublic ThreadPrivate ThreadForum2]`;
			const res = await ctx.client.http.POST("/api/v1/search/channels", {
				body: {
					query: tantivyQuery,
					limit: 50,
					offset: 0,
				},
			});
			return res.data?.channels ?? [];
		} catch (e) {
			console.error("Search failed", e);
			return [];
		}
	});

	const onSearchInput = (value: string) => {
		setSearch(value);
		debouncedSetSearch(value);
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Enter") {
			debouncedSetSearch.clear();
			setDebouncedSearch(search());
		} else if (e.key === "Escape") {
			if (search().length > 0) {
				setSearch("");
				setDebouncedSearch("");
				e.stopPropagation();
			} else {
				ctx.setThreadsView(null);
			}
		}
	};

	const activeThreads = threads2.useListForChannel(() => props.channel_id);
	const archivedThreads = threads2.useListArchivedForChannel(
		() => props.channel_id,
	);

	const sortedThreads = createMemo(() => {
		if (search().length > 0) {
			const joined: Channel[] = [];
			const notJoined: Channel[] = [];
			const archived: Channel[] = [];

			for (const t of searchResults() ?? []) {
				if (t.archived_at) {
					archived.push(t);
				} else if (t.thread_member) {
					joined.push(t);
				} else {
					notJoined.push(t);
				}
			}

			return {
				joined,
				notJoined,
				archived,
			};
		}

		const activeList = activeThreads()?.state.ids ?? [];
		const archivedList = archivedThreads()?.state.ids ?? [];

		const active = activeList
			.map((id) => channels2.cache.get(id))
			.filter((t): t is Channel => t !== undefined);
		const archived = archivedList
			.map((id) => channels2.cache.get(id))
			.filter(
				(t): t is Channel =>
					t !== undefined &&
					t.archived_at !== undefined &&
					t.archived_at !== null,
			);

		const query = search();
		const joined: Channel[] = [];
		const notJoined: Channel[] = [];

		if (query) {
			const filteredActive = go(query, active, {
				key: "name",
				threshold: -10000,
			}).map((r) => r.obj);

			for (const t of filteredActive) {
				if (t.thread_member) {
					joined.push(t);
				} else {
					notJoined.push(t);
				}
			}
		} else {
			for (const t of active) {
				if (t.thread_member) {
					joined.push(t);
				} else {
					notJoined.push(t);
				}
			}
		}

		return {
			joined,
			notJoined,
			archived,
		};
	});
	const isLoading = () =>
		activeThreads.loading || archivedThreads.loading || searchResults.loading;
	const isEmpty = () =>
		!isLoading() &&
		sortedThreads().joined.length === 0 &&
		sortedThreads().notJoined.length === 0 &&
		sortedThreads().archived.length === 0;

	const onCreateThread = () => {
		const channel_id = props.channel_id;
		const channel = channels2.cache.get(channel_id)!;
		ctx.setThreadsView(null);
		modalctl.open({
			type: "thread_create",
			room_id: channel.room_id!,
			channel_id,
		});
	};

	const onThreadClick = (thread: Channel) => {
		nav(`/thread/${thread.id}`);
		ctx.setThreadsView(null);
	};

	const ThreadSkeletonItem = () => (
		<div class="thread-item skeleton">&nbsp;</div>
	);

	const skeletonItems = Array.from({ length: 5 }, (_, i) => ({
		id: `thread-skeleton-${i}`,
	}));

	let searchInputRef: HTMLInputElement | undefined;

	onMount(() => {
		searchInputRef?.focus();
	});

	const ThreadItem = (props: { thread: Channel }) => {
		return (
			<div class="thread-item" onClick={[onThreadClick, props.thread]}>
				<ChannelIcon channel={props.thread} />
				<div>
					<div>{props.thread.name}</div>
					<div class="dim">
						Active{" "}
						<Time
							date={getTimestampFromUUID(
								props.thread.last_message_id ?? props.thread.id,
							)}
						/>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div class="threads-popout" onClick={(e) => e.stopPropagation()}>
			<div class="background"></div>
			<div class="content">
				<div class="header">
					<input
						ref={searchInputRef}
						type="search"
						placeholder="Search threads..."
						value={search()}
						onInput={(e) => onSearchInput(e.currentTarget.value)}
						onKeyDown={onKeyDown}
						class="search-pad"
					/>
					<button type="button" class="primary button" onClick={onCreateThread}>
						create thread
					</button>
				</div>
				<div class="thread-list">
					<Show when={isLoading()}>
						<h3 class="dim">loading threads...</h3>
						<For each={skeletonItems}>{() => <ThreadSkeletonItem />}</For>
					</Show>
					<Show when={!isLoading()}>
						<Show when={sortedThreads().joined.length}>
							<h3 class="dim">joined threads</h3>
						</Show>
						<For each={sortedThreads().joined}>
							{(thread) => <ThreadItem thread={thread} />}
						</For>
						<Show when={sortedThreads().notJoined.length}>
							<h3 class="dim">active threads</h3>
						</Show>
						<For each={sortedThreads().notJoined}>
							{(thread) => <ThreadItem thread={thread} />}
						</For>
						<Show when={sortedThreads().archived.length}>
							<h3 class="dim">archived threads</h3>
						</Show>
						<For each={sortedThreads().archived}>
							{(thread) => <ThreadItem thread={thread} />}
						</For>
						<Show when={isEmpty()}>
							<div
								class="dim"
								style="text-align:center;margin:2em;font-size:1rem;"
							>
								no threads :(
							</div>
						</Show>
					</Show>
				</div>
			</div>
		</div>
	);
};
